import { z } from "zod";
import { ACCESS_VOCABULARY_PARAM, ACCESS_VOCABULARY_VERSION } from "#src/types/auth.ts";
import type { ConnectorCatalogItem } from "#src/types/connectors.ts";
import { isDataCapability } from "#src/types/connectorCapabilities.ts";

/**
 * Where an account sits for the connector prompt (`docs/prds/onboarding.md`,
 * The connect modal): `none` reaches no data, `core` reaches only mail,
 * calendar or contacts, `extended` reaches anything else and is never prompted.
 */
export type ConnectorPromptState = "none" | "core" | "extended";

/** The states that get a prompt. */
export const PROMPTED_STATES = ["none", "core"] as const;
export type PromptedState = (typeof PROMPTED_STATES)[number];

/** The kinds of data every assistant needs first, in the order the prompt asks for them. */
export const CORE_CONNECTOR_KINDS = ["calendar", "email", "contacts"] as const;
export type CoreConnectorKind = (typeof CORE_CONNECTOR_KINDS)[number];

const CORE_CAPABILITY_PREFIX: Record<CoreConnectorKind, string> = {
  calendar: "calendar:",
  email: "email:",
  contacts: "contact:",
};

/**
 * The catalog items that connect each core kind. Contacts has none: on the web
 * it arrives with a Google or Microsoft account, and on iOS it is the device's.
 */
export const CORE_CATALOG_ITEM_IDS: Record<CoreConnectorKind, readonly string[]> = {
  calendar: ["provider:google", "provider:microsoft"],
  email: ["email_inbox:google", "email_inbox:microsoft"],
  contacts: [],
};

/**
 * Catalog items a prompt never recommends, beside every mailbox: the calendar
 * items, which it asks for by kind, and entries that are not a service of the
 * user's to connect.
 */
const NEVER_RECOMMENDED_IDS = new Set([
  ...CORE_CATALOG_ITEM_IDS.calendar,
  "custom_mcp",
  "provider:link",
]);

/** Whether a catalog item may be recommended by the prompt: not core, not connected, not custom. */
export function isRecommendableCatalogItem(item: ConnectorCatalogItem): boolean {
  return (
    !NEVER_RECOMMENDED_IDS.has(item.id) &&
    !item.id.startsWith("email_inbox:") &&
    !item.custom &&
    !item.connected
  );
}

/** The prompt state for an account holding these capabilities. Pure. */
export function connectorPromptState(capabilities: readonly string[]): ConnectorPromptState {
  const data = capabilities.filter(isDataCapability);
  if (data.length === 0) return "none";
  const prefixes = Object.values(CORE_CAPABILITY_PREFIX);
  return data.every((capability) => prefixes.some((prefix) => capability.startsWith(prefix)))
    ? "core"
    : "extended";
}

/** The core kinds these capabilities do not reach, in the order the prompt asks for them. */
export function missingCoreKinds(capabilities: readonly string[]): CoreConnectorKind[] {
  return CORE_CONNECTOR_KINDS.filter(
    (kind) =>
      !capabilities.some((capability) => capability.startsWith(CORE_CAPABILITY_PREFIX[kind])),
  );
}

/** The providers a sign-in, and so the prompt's core connectors, can come from. */
export const PROMPT_PROVIDERS = ["google", "microsoft"] as const;
export type PromptProvider = (typeof PROMPT_PROVIDERS)[number];

/** GET /api/a/connector-prompt */
export type GetConnectorPromptResp =
  | { due: false }
  | {
      due: true;
      state: PromptedState;
      missingCore: CoreConnectorKind[];
      /**
       * The provider the core connectors come from: the one the user signed in
       * with, or, for a phone or email signup, whichever Google or Microsoft
       * account they connected. Null when they have neither.
       */
      provider: PromptProvider | null;
      /** The connector the user signed in with, which a core addition re-auths. Null for a phone or email signup. */
      signInConnectorId: number | null;
      /** For `core`: the picker's Recommended items for what core is missing, before the picks. */
      pinnedIds: string[];
    };

/**
 * The catalog items for the core kinds an account is missing, from its own
 * provider: missing calendar is that provider's calendar, missing mail its
 * mailbox. Contacts come with either, so they pin nothing. With no provider
 * both providers' items are offered. Pure.
 */
export function corePinnedIds(
  missingCore: readonly CoreConnectorKind[],
  provider: PromptProvider | null,
): string[] {
  const providers = provider ? [provider] : PROMPT_PROVIDERS;
  return [
    ...(missingCore.includes("calendar") ? providers.map((p) => `provider:${p}`) : []),
    ...(missingCore.includes("email") ? providers.map((p) => `email_inbox:${p}`) : []),
  ];
}

/** How many services the core prompt's Recommended list always shows. */
export const PROMPT_RECOMMENDED_SLOTS = 4;

/**
 * The core prompt's Recommended list: the missing core items first, then the
 * picks, filling {@link PROMPT_RECOMMENDED_SLOTS} in all. Pure.
 */
export function promptRecommendedIds(
  pinnedIds: readonly string[],
  pickIds: readonly string[],
): string[] {
  const picks = pickIds.filter((id) => !pinnedIds.includes(id));
  return [...pinnedIds, ...picks].slice(0, Math.max(PROMPT_RECOMMENDED_SLOTS, pinnedIds.length));
}

/**
 * Where the no-connector prompt's Continue goes for the access the user left
 * ticked, in one provider consent. A user who signed in with this provider has
 * their sign-in connector re-authed with the additions. Anyone else adds a new
 * connector: the mailbox route when mail is ticked and the calendar route
 * otherwise, each told the ticks so it asks only for those. Pure.
 */
export function accessContinuePath({
  provider,
  signInConnectorId,
  access,
}: {
  provider: PromptProvider;
  signInConnectorId: number | null;
  access: readonly string[];
}): string {
  if (signInConnectorId !== null) {
    return `/api/a/auth/${provider}/reauth/${signInConnectorId}?add=${access.join(",")}`;
  }
  const route = access.includes("email") ? "add-email-inbox" : "add-connector";
  return `/api/a/auth/${provider}/${route}?access=${access.join(",")}&${ACCESS_VOCABULARY_PARAM}=${ACCESS_VOCABULARY_VERSION}`;
}

export const getConnectorPromptPicksQuery = z.object({
  platform: z.enum(["web", "ios"]).default("web"),
});

/** GET /api/a/connector-prompt/picks */
export type GetConnectorPromptPicksResp = {
  items: ConnectorCatalogItem[];
  /** `fallback` when Jev could not score the catalog and a fixed set was used. */
  source: "jev" | "fallback";
};
